/**
 * Firewall-allowlist hint policy for a failed connection test (THUB-138).
 *
 * SCOPE: this module decides WHETHER to surface the "ask the partner to allowlist our egress
 * IPs" hint. It no longer owns the failure WORDING -- THUB-243's `connectionTestGuidance.ts`
 * does, and that one is transport-aware (it will not tell an Azure Blob user to check an SSH
 * key, and it prefers Azure's own error code over our flattened AUTH_FAILED). This module's
 * original `ERROR_CODE_MESSAGES` map was a flat errorCode->string lookup and was strictly
 * superseded, so it was removed rather than left to drift alongside its replacement -- which
 * is the very failure mode THUB-138 set out to eliminate.
 *
 * The two rules that survive, and are load-bearing:
 *
 * 1. Only a genuine reachability failure may tell the operator to chase the partner's
 *    firewall. That hint is expensive -- it starts a conversation with an external party --
 *    so it must never appear for a host-key mismatch, an auth failure, a DNS typo, or a
 *    missing path. `ALLOWLIST_HINT_ERROR_CODES` is that allowlist, and the backend classifier
 *    (connection_management_api/index.py) is ordered to match.
 *
 * 2. Never hardcode an egress IP. There are two unrelated mechanisms (Transfer Family
 *    connector egress IPs, AWS-assigned at runtime and persisted per-connection as
 *    `connectorEgressIps`; and VPC NAT EIPs, used only for HTTP/Azure and never in the test
 *    path). Neither is a constant. The hint points at the existing "Firewall Whitelist IPs"
 *    panel on the connection Overview tab, which already renders `connectorEgressIps`.
 */

/**
 * The label of the existing panel that renders a connection's egress IPs. Kept as a constant
 * so the cross-reference in the copy below and the panel heading in ConnectionDetails.tsx
 * cannot silently drift apart.
 */
export const FIREWALL_PANEL_LABEL = 'Firewall Whitelist IPs';

/**
 * Error codes for which "ask the partner to allowlist our egress IPs" is genuinely the right
 * next step: the connection did not get through at the network layer.
 *
 * Deliberately excludes ENDPOINT_ERROR (the endpoint answered us, so it is reachable),
 * HOST_KEY_MISMATCH and AUTH_FAILED (we reached and talked to the server), DNS_FAILURE (the
 * name did not resolve, so no packet was ever blocked), and the directory-stage codes.
 */
export const ALLOWLIST_HINT_ERROR_CODES: ReadonlySet<string> = new Set([
  'NETWORK_UNREACHABLE',
  'CONNECTION_REFUSED',
  'TIMEOUT',
]);

/** Whether this failure warrants pointing the operator at partner-firewall allowlisting. */
export function shouldShowAllowlistHint(errorCode: string | undefined): boolean {
  return !!errorCode && ALLOWLIST_HINT_ERROR_CODES.has(errorCode);
}
